import { Injectable, inject, signal } from '@angular/core';
import { Params, Router } from '@angular/router';
import { formatDate } from 'date-fns/format';
import { Observable, finalize, map, tap } from 'rxjs';

import {
  CalendarRoomRow,
  RoomQueryParams,
  RoomsApiService,
} from '@bookly/shared';

@Injectable({
  providedIn: 'root',
})
export class UserCalendarService {
  readonly #roomsApiService = inject(RoomsApiService);
  readonly #router = inject(Router);

  readonly #calendarRows = signal<CalendarRoomRow[]>([]);
  readonly #isLoading = signal(false);

  readonly calendarRows = this.#calendarRows.asReadonly();

  get isLoading() {
    return this.#isLoading.asReadonly();
  }

  /**
   * Loads the calendar items for the given week. The search criteria are read
   * from the current URL query params (see {@link #buildCriteriaFromUrl}); the
   * backend groups the results by room and does the filtering.
   */
  public loadBookings(weekStart: Date, weekEnd: Date): Observable<unknown> {
    this.#isLoading.set(true);
    const criteria = this.#buildCriteriaFromUrl();
    return this.#roomsApiService
      .getUserCalendarItems(
        formatDate(weekStart, 'yyyy-MM-dd'),
        formatDate(weekEnd, 'yyyy-MM-dd'),
        criteria
      )
      .pipe(
        tap(rows => this.#calendarRows.set(rows)),
        finalize(() => this.#isLoading.set(false)),
        map(() => undefined)
      );
  }

  public clear(): void {
    this.#calendarRows.set([]);
  }

  /**
   * Reads the search criteria from the current URL query params, keeping only
   * the params the calendar endpoint supports. `roomTypes` and `featureIds`
   * are stored comma-separated in the URL and are parsed back into arrays.
   *
   * On the favorites route (`/dashboard/favorites`) all other filters are
   * ignored and the calendar always requests the user's favorite rooms only.
   */
  #buildCriteriaFromUrl(): RoomQueryParams {
    if (this.#isFavoriteRoute()) {
      return { favorite: true };
    }
    const params: Params = this.#router.parseUrl(this.#router.url).queryParams;
    const criteria: RoomQueryParams = {};

    if (params['roomName']) {
      criteria.roomName = params['roomName'];
    }
    if (params['minCapacity']) {
      criteria.minCapacity = +params['minCapacity'];
    }
    if (params['maxCapacity']) {
      criteria.maxCapacity = +params['maxCapacity'];
    }
    if (params['floorId']) {
      criteria.floorId = +params['floorId'];
    }
    if (params['siteId']) {
      criteria.siteId = +params['siteId'];
    }
    if (params['buildingId']) {
      criteria.buildingId = +params['buildingId'];
    }
    if (params['favorite']) {
      criteria.favorite = params['favorite'] === 'true';
    }
    if (params['roomTypes']) {
      criteria.roomTypes = params['roomTypes'].split(',');
    }
    if (params['featureIds']) {
      criteria.featureIds = params['featureIds'].split(',').map(Number);
    }
    if (params['fromDate']) {
      criteria.fromDate = params['fromDate'];
    }
    if (params['toDate']) {
      criteria.toDate = params['toDate'];
    }

    return criteria;
  }

  /** Whether the calendar is shown on the favorites route (`/dashboard/favorites`). */
  #isFavoriteRoute(): boolean {
    return (
      this.#router.url?.split('?')[0].split('/').includes('favorites') ?? false
    );
  }
}
